import { AI_HELP_RESERVED_SPACE_EVENT, readAiHelpReservedSpacePx } from '@constants/layout'
import { useCallback, useEffect, useRef, useState } from 'react'

// Equal columns plus the row gap (~840px). Stack only once they can no longer
// sit side by side.
export const DASHBOARD_STACK_WIDTH = 840
export const DASHBOARD_NARROW_WIDTH = 760
// Four in-column action labels need ~640px. Full-width QA starts above this
// so the buttons are not squeezed next to Cash.
export const DASHBOARD_WIDE_WIDTH = 1280
// Needs you | Deadlines and Capital | Invoices wrap at the same min column.
export const DASHBOARD_PAIR_MIN = '20rem'
// VAT strip month-tracks. Quarters span 3; the ladder is 12 → 6 → 3 with
// WHEN_STACKED / WHEN_NARROW. Extra space is 1fr, not a second observer.
export const VAT_MONTH_TRACKS = 12
export const VAT_MONTH_TRACKS_STACKED = 6
export const VAT_MONTH_TRACKS_NARROW = 3

export const WHEN_STACKED = "[data-dashboard-stacked='true'] &"
export const WHEN_NARROW = "[data-dashboard-narrow='true'] &"
export const WHEN_WIDE = "[data-dashboard-wide='true'] &"

export interface DashboardWidth {
  isStacked: boolean
  isNarrow: boolean
  isWide: boolean
}

const SHEET_ANIMATION_MS = 260

/**
 * Column width observer. Callback ref because the column mounts inside a
 * content loader; a `[]` effect would run while the node is still null.
 * Styled-components 5 cannot emit `@container`, so layout is attribute-based.
 * Attributes stay on at every width, including the mobile viewport.
 */
export const useDashboardWidth = () => {
  const [width, setWidth] = useState<DashboardWidth>({
    isStacked: false,
    isNarrow: false,
    isWide: true
  })
  const observerRef = useRef<ResizeObserver | null>(null)
  const elementRef = useRef<HTMLDivElement | null>(null)
  const reservedRef = useRef(0)
  const ignoreUntilRef = useRef(0)

  const measure = useCallback((value: number) => {
    const next = {
      isStacked: value <= DASHBOARD_STACK_WIDTH,
      isNarrow: value <= DASHBOARD_NARROW_WIDTH,
      isWide: value > DASHBOARD_WIDE_WIDTH
    }
    setWidth(current =>
      current.isStacked === next.isStacked &&
      current.isNarrow === next.isNarrow &&
      current.isWide === next.isWide
        ? current
        : next
    )
  }, [])

  const ref = useCallback(
    (element: HTMLDivElement | null) => {
      observerRef.current?.disconnect()
      observerRef.current = null
      if (!element || typeof ResizeObserver === 'undefined') return

      const observer = new ResizeObserver(entries => {
        if (Date.now() < ignoreUntilRef.current) return
        const entry = entries[0]
        if (entry) measure(entry.contentRect.width)
      })
      observer.observe(element)
      observerRef.current = observer
      elementRef.current = element
      reservedRef.current = readAiHelpReservedSpacePx()
      measure(element.getBoundingClientRect().width)
    },
    [measure]
  )

  useEffect(() => {
    const onReservedSpaceChange = () => {
      const element = elementRef.current
      if (!element) return

      const reserved = readAiHelpReservedSpacePx()
      const delta = reserved - reservedRef.current
      reservedRef.current = reserved
      if (delta === 0) return

      measure(element.getBoundingClientRect().width - delta)
      ignoreUntilRef.current = Date.now() + SHEET_ANIMATION_MS
    }

    window.addEventListener(AI_HELP_RESERVED_SPACE_EVENT, onReservedSpaceChange)
    return () => {
      window.removeEventListener(AI_HELP_RESERVED_SPACE_EVENT, onReservedSpaceChange)
      observerRef.current?.disconnect()
    }
  }, [measure])

  return { ref, ...width }
}
